import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import passwordValidator from 'password-validator';
import {
  showToast,
  verifyRecaptcha,
  validateConfirmPassword
} from '../../../utils/user/patient/helpers';
import { MESSAGES } from '../../../utils/user/patient/config';
import { useDispatch, useSelector } from 'react-redux';
// import { createSelector } from 'reselect';
import { registerPatient } from '../../../redux/middleware/user/patient/auth/register';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { CircularProgress } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { Google } from '@mui/icons-material';
// import { Google, GoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff } from 'react-feather';
import Divider from '../../../components/patient/misc/Divider';
import { useNavigate } from 'react-router-dom';
import { APP_URLS } from '../../../../urls';
import useGoogleAuth from '../hooks/useGoogleAuth';
import { patientVerifyCaptcha } from '../../../redux/middleware/user/patient/captcha';

// Password validation schema
const passwordValidationSchema = new passwordValidator();
passwordValidationSchema
  .is()
  .min(8)
  .has()
  .letters() // At least one letter (any case)
  .has()
  .digits()
  .has()
  .symbols()
  .has()
  .lowercase() // At least one lowercase specifically
  .has()
  .uppercase() // At least one uppercase specifically
  .has()
  .not()
  .spaces();

// Create the Yup validation schema
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string()
    .required('Phone number is required')
    .test('isValidPhoneNumber', 'Invalid phone number', (value) =>
      isValidPhoneNumber(`+${value}` || '')
    ),
  password: Yup.string()
    .required('Password is required')
    .test(
      'isStrongPassword',
      'Password must include at least 8 characters, one number, and one special character',
      (value) => passwordValidationSchema.validate(value)
    ),
  confirmPassword: Yup.string().required('Confirm password is required')
});

const Registration = ({ toast }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    if (captchaRef.current) {
      captchaRef.current.reset();
      setCaptchaToken(null);
    }
  };

  const { loading, patientData, error } = useSelector(
    (state) => state.patientRegister
  );
  const { isAuthenticated } = useSelector((state) => state.patientAuth);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(APP_URLS.home);
    }
  }, [isAuthenticated, navigate]);

  // useEffect(() => {
  // 	if (error) showToast(toast, 'error', error);
  // 	if (patientData) {
  // 		// console.log('Patients Data: ', patientData);
  // 		showToast(toast, 'success', MESSAGES.loginSuccess);
  // 	}
  // }, [error, patientData]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [errors, setErrors] = useState({});
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    googleUserInfo,
    loading: googleLoading,
    handleGoogleSignIn
  } = useGoogleAuth(dispatch, toast);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: ''
    });
    setErrors({});
    setAgreeTerms(false);
    resetCaptcha();
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value
    }));

    if (name === 'confirmPassword') {
      await validateConfirmPassword(formData.password, value, setErrors);
    }

    try {
      await validationSchema.validateAt(name, { [name]: value });
      setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: error.message }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prevState) => ({
      ...prevState,
      phone: value
    }));
    setErrors((prevErrors) => ({ ...prevErrors, phone: '' }));
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate confirm password
    await validateConfirmPassword(
      formData.password,
      formData.confirmPassword,
      setErrors
    );

    // Check for errors
    if (Object.values(errors).some((error) => error !== '')) {
      showToast(toast, 'error', 'Please fix the errors in the form.');
      return;
    }

    if (!captchaToken) {
      showToast(toast, 'error', 'Please complete the CAPTCHA');
      return;
    }

    const captchaResult = await dispatch(
      patientVerifyCaptcha(captchaToken, (intent, message) =>
        showToast(toast, intent, message)
      )
    );

    if (!captchaResult.success) {
      resetCaptcha();
      return;
    }

    try {
      // Validate form data
      await validationSchema.validate(formData, { abortEarly: false });

      // Prepare form data for API
      const updatedFormData = {
        ...formData,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone_number: formData.phone
      };
      delete updatedFormData.firstName;
      delete updatedFormData.lastName;
      delete updatedFormData.phone;

      // Dispatch registration request
      const response = await dispatch(registerPatient(updatedFormData));

      if (response.status === 201) {
        resetForm();
        navigate(APP_URLS.patientLogin);
        showToast(
          toast,
          'success',
          response?.data?.message || 'Registration successful!'
        );
      } else {
        showToast(
          toast,
          'error',
          response?.data?.message || 'Unexpected server response.'
        );
      }
    } catch (error) {
      if (error.name === 'ValidationError') {
        // Handle Yup validation errors
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrors(validationErrors);
      } else if (error.response?.status === 400) {
        // Handle backend validation errors
        showToast(
          toast,
          'error',
          error.response.data?.message ||
						'Failed to register. Please try again.'
        );
      } else {
        // Handle unexpected errors
        showToast(toast, 'error', 'An unexpected error occurred.');
      }
    }
    // Reset reCAPTCHA on any error
    resetCaptcha();
  };

  return (
    <form onSubmit={handleSubmit} className={css(styles.form)}>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <input
          type='text'
          name='firstName'
          placeholder='First Name'
          value={formData.firstName}
          onChange={handleChange}
          className={css(styles.input)}
        />
        {errors.firstName && (
          <p className={css(styles.error)}>{errors.firstName}</p>
        )}
      </motion.div>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <input
          type='text'
          name='lastName'
          placeholder='Last Name'
          value={formData.lastName}
          onChange={handleChange}
          className={css(styles.input)}
        />
        {errors.lastName && (
          <p className={css(styles.error)}>{errors.lastName}</p>
        )}
      </motion.div>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <input
          type='email'
          name='email'
          placeholder='Email'
          value={formData.email}
          onChange={handleChange}
          className={css(styles.input)}
        />
        {errors.email && <p className={css(styles.error)}>{errors.email}</p>}
      </motion.div>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
      >
        <PhoneInput
          country='ke'
          onlyCountries={['ke']}
          autoFormat
          value={formData.phone}
          dropdownClass={css(styles.phoneDropdownCustom)}
          onChange={handlePhoneChange}
          containerClass={css(styles.phoneInputContainer)}
          inputClass={css(styles.phoneInput, styles.phoneInputCustom)}
        />
        {errors.phone && <p className={css(styles.error)}>{errors.phone}</p>}
      </motion.div>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.5 }}
      >
        <div className={css(styles.passwordInputWrapper)}>
          <input
            type={showPassword ? 'text' : 'password'}
            name='password'
            placeholder='Password'
            value={formData.password}
            onChange={handleChange}
            className={css(styles.input, styles.passwordInput)}
          />
          <button
            type='button'
            onClick={() => setShowPassword((prev) => !prev)}
            className={css(styles.passwordToggle)}
          >
            {showPassword
              ? (
                <EyeOff className={css(styles.eyeIcon)} />
                )
              : (
                <Eye className={css(styles.eyeIcon)} />
                )}
          </button>
        </div>
        {errors.password && (
          <p className={css(styles.error)}>{errors.password}</p>
        )}
      </motion.div>
      <motion.div
        className={css(styles.inputGroup)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        <div className={css(styles.passwordInputWrapper)}>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            name='confirmPassword'
            placeholder='Confirm Password'
            value={formData.confirmPassword}
            onChange={handleChange}
            className={css(styles.input, styles.passwordInput)}
          />
          <button
            type='button'
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            className={css(styles.passwordToggle)}
          >
            {showConfirmPassword
              ? (
                <EyeOff className={css(styles.eyeIcon)} />
                )
              : (
                <Eye className={css(styles.eyeIcon)} />
                )}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className={css(styles.error)}>{errors.confirmPassword}</p>
        )}
      </motion.div>
      <motion.label
        className={css(styles.checkboxLabel)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.7 }}
      >
        <input
          type='checkbox'
          checked={agreeTerms}
          onChange={() => setAgreeTerms(!agreeTerms)}
          required
        />
        I agree to the Terms and Privacy Policy
      </motion.label>
      <motion.div
        className={css(styles.captcha)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={process.env.GOOGLE_RECAPTCH_SITE_KEY}
          onChange={handleCaptchaChange}
        />
      </motion.div>
      <motion.button
        type='submit'
        className={css(styles.button)}
        disabled={!agreeTerms || loading}
        whileHover={loading ? {} : { scale: 1.05 }}
        whileTap={loading ? {} : { scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.8 }}
      >
        {loading
          ? (
            <CircularProgress size={24} color='inherit' />
            )
          : (
				  'Create Account'
            )}
      </motion.button>
      <Divider />
      {/* <motion.div
				whileHover={{ scale: 1.05 }}
				whileTap={{ scale: 0.95 }}
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3, delay: 0.5 }}
			>
				<GoogleLogin
					logo_alignment="center"
					text="signup_with"
					// login_uri={APP_URLS.patientDashboard}
					onSuccess={(credentialResponse) => {
						console.log(credentialResponse);
						showToast(toast, 'success', 'Login successful');
						navigate(APP_URLS.home);
					}}
					onError={(error) => {
						console.error('Login Failed', error);
						showToast(toast, 'error', `Login failed: ${error.error}`);
					}}
				/>
			</motion.div> */}
      <motion.button
        type='button'
        onClick={handleGoogleSignIn}
        className={css(styles.googleButton)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        disabled={loading || googleLoading}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.4 }}
      >
        <Google style={{ marginRight: '0.5rem', fontSize: '1.5rem' }} />
        {googleLoading
          ? (
            <CircularProgress size={24} color='inherit' />
            )
          : (
				  'Sign in with Google'
            )}
      </motion.button>
    </form>
  );
};

const styles = StyleSheet.create({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  inputGroup: {
    position: 'relative'
  },
  captcha: {
    display: 'flex',
    justifyContent: 'start',
    marginBottom: '1rem'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    transition: 'border-color 0.3s',
    ':focus': {
      outline: 'none',
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)'
    }
  },
  phoneInputContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '0.25rem',
    ':focus-within': {
      borderColor: '#2783F7',
      boxShadow: '0 0 0 3px rgba(39, 131, 247, 0.1)'
    },
    backgroundColor: '#00000'
  },
  phoneInput: {
    width: '100%',
    padding: '0.75rem',
    paddingLeft: '50px',
    border: 'none',
    fontSize: '1rem',
    outline: 'none'
  },
  phoneInputCustom: {
    '.form-control': {
      paddingLeft: '50px'
    }
  },
  phoneDropdownCustom: {
    color: 'black'
  },
  passwordInputWrapper: {
    position: 'relative',
    width: '100%'
  },
  passwordInput: {
    paddingRight: '40px'
  },
  passwordToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#4a5568'
  },
  eyeIcon: {
    color: 'black',
    width: '1.15rem',
    height: '1.15rem'
  },
  button: {
    backgroundColor: '#2783F7',
    color: 'white',
    padding: '0.75rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    ':hover': {
      backgroundColor: '#1c5fa8'
    },
    ':disabled': {
      backgroundColor: '#a0aec0',
      cursor: 'not-allowed'
    }
  },
  googleButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
    color: '#4a5568',
    border: '1px solid #e2e8f0',
    padding: '0.75rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s, border-color 0.3s',
    ':hover': {
      backgroundColor: '#f7fafc',
      borderColor: '#cbd5e0'
    }
  },
  error: {
    color: '#e53e3e',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
    // position: 'absolute',
    bottom: '-1.25rem',
    left: '0'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#4a5568'
  }
});

export default Registration;
